import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  X,
  ChevronDown,
  Sparkles,
  Phone,
  Mail,
  ArrowUpRight,
  MoveRight,
} from "lucide-react";
import { CountryContext, CountryStatusContext } from "../../CountryContext";
import { getOfficeForCountry } from "../../Config/officeLocations";

const Head = () => {
  const location = useLocation();
  const country = useContext(CountryContext);
  const { loading: countryLoading } = useContext(CountryStatusContext);
  const office = getOfficeForCountry(country);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const aiFeatures = [
    {
      name: "Law and Legal",
      path: "/ai-features/law-and-legal",
    },
    {
      name: "Manufacturing",
      path: "/ai-features/manufacturing",
    },
    {
      name: "Healthcare",
      path: "/ai-features/healthcare",
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 w-full mb-0">

      {/* TOP BAR */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-slate-950 text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2">

            {/* LEFT */}
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 text-[0.625rem] sm:text-sm text-slate-200">
              {!countryLoading && office.phone && (
                <>
                  <a
                    href={office.phone.href}
                    className="flex items-center gap-2 rounded-full px-3 py-1.5 hover:bg-white/10 hover:text-white transition-all duration-300"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/15 text-blue-300">
                      <Phone size={13} />
                    </span>
                    {office.phone.display}
                  </a>

                  <span className="hidden sm:block h-4 w-px bg-white/20"></span>
                </>
              )}

              <a
                href="mailto:enquiry@thinkquotient.ai"
                className="flex items-center gap-2 rounded-full px-3 py-1.5 hover:bg-white/10 hover:text-white transition-all duration-300"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500/15 text-purple-300">
                  <Mail size={13} />
                </span>
                enquiry@thinkquotient.ai
              </a>
            </div>

            {/* RIGHT */}
            <div className="hidden md:flex items-center gap-2 rounded-full bg-white/8 border border-white/10 px-3 py-1.5 text-xs text-slate-200">
              <Sparkles size={14} className="text-blue-300" />
              AI Automation Solutions For Businesses
            </div>

          </div>
        </div>
      </div>

      {/* MAIN NAVBAR */}
      <div className="bg-white/90 backdrop-blur-2xl border-b border-slate-200/80 shadow-[0_8px_30px_rgba(15,23,42,0.06)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <div className="flex items-center justify-left h-20">

            {/* LOGO */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="bg-white rounded-2xl p-2.5 shadow-sm border border-slate-200/80 transition-all duration-300 group-hover:shadow-md group-hover:-translate-y-0.5">
                <img
                  src="/assets/images/envelop.png"
                  alt="ThinkQuotient"
                  className="h-8 sm:h-10 object-contain"
                />
              </div>
            </Link>

            {/* DESKTOP NAV */}
            <nav className="hidden md:flex items-center gap-4  p-1.5 ms-6">

              {/* HOME */}
              <Link
                to="/"
                className={`relative px-4 pt-2.5 pb-1  text-sm font-semibold transition-all duration-300
                  ${isActive("/")
                    ? "text-blue-600 after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-blue-600"
                    : "text-slate-700 hover:text-blue-600"
                  }`}
              >
                Home
              </Link>

                {/* ABOUT US */}
              <Link
                to="/about-us"
                className={`relative px-4 pt-2.5 pb-1  text-sm font-semibold transition-all duration-300
                  ${isActive("/about-us")
                    ? "text-blue-600 after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-blue-600"
                    : "text-slate-700 hover:text-blue-600"
                  }`}
              >
                About Us
              </Link>

              {/* CONTACT US */}
              <Link
                to="/contact-us"
                className={`relative px-4 pt-2.5 pb-1 text-sm font-semibold transition-all duration-300
                  ${isActive("/contact-us")
                    ? "text-blue-600 after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-blue-600"
                    : "text-slate-700 hover:text-blue-600"
                  }`}
              >
                Contact Us
              </Link>

              {/* AI FEATURES */}
              <div className="relative group">
                <button
                  className={`flex items-center gap-2 px-5 pt-2.5 pb-1  rounded-xl text-sm font-semibold transition-all duration-300
                  ${location.pathname.includes("/ai-features")
                      ? "text-blue-600 after:absolute after:left-4 after:right-4 after:-bottom-1 after:h-[3px] after:rounded-full after:bg-blue-600"
                      : "text-slate-700 hover:text-blue-600"
                    }`}
                >
                  AI Features
                  <ChevronDown
                    size={16}
                    className="transition-transform duration-300 group-hover:rotate-180"
                  />
                </button>

                {/* DROPDOWN */}
                <div className="absolute left-0 top-full pt-5 opacity-0 invisible translate-y-3 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out">
                  <div className="w-80 rounded-2xl border border-slate-200/80 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.14)] overflow-hidden">

                    {/* Dropdown top accent */}
                    <div className="relative h-2 overflow-hidden bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500">
                      <div className="absolute inset-0 bg-white/20"></div>
                    </div>

                    <div className="p-2.5">
                      {aiFeatures.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          className="group/item flex items-center justify-between px-4 py-3.5 rounded-xl text-sm font-semibold text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-all duration-200"
                        >
                          <span>{item.name}</span>

                          <span className="relative flex h-7 w-7 items-center justify-center rounded-full bg-slate-50 transition-all duration-300 group-hover/item:bg-blue-50">
                            <span className="h-2 w-2 rounded-full bg-slate-300 transition-all duration-300 group-hover/item:h-2.5 group-hover/item:w-2.5 group-hover/item:bg-blue-600"></span>
                          </span>
                        </Link>
                      ))}
                    </div>

                  </div>
                </div>
              </div>

            </nav>

            {/* CTA + MOBILE */}
            <div className="flex items-center gap-3 justify-end ms-auto">

              {/* OPTIONAL CTA BUTTON */}
              {/* <Link
                to="/registerWebinar"
                className="hidden sm:inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-5 py-3 text-sm font-semibold shadow-[0_12px_30px_rgba(15,23,42,0.22)] hover:bg-slate-800 hover:-translate-y-0.5 transition-all duration-300"
              >
                <Sparkles size={16} />
                Free Webinar
              </Link> */}

              {/* MOBILE BUTTON */}
              <button
                onClick={toggleMobileMenu}
                className="md:hidden w-11 h-11 rounded-2xl border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-700 hover:bg-slate-50 hover:shadow-md transition-all duration-300"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>

            </div>

          </div>
        </div>
      </div>

      {/* MOBILE MENU */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-all duration-300
        ${mobileMenuOpen
            ? "opacity-100 visible"
            : "opacity-0 invisible"
          }`}
      >

        {/* OVERLAY */}
        <div
          className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm"
          onClick={toggleMobileMenu}
        ></div>

        {/* SIDEBAR */}
        <div
          className={`absolute right-0 top-0 h-full w-[85%] sm:w-[400px] bg-white shadow-[0_25px_80px_rgba(15,23,42,0.35)] transition-transform duration-300
          ${mobileMenuOpen
              ? "translate-x-0"
              : "translate-x-full"
            }`}
        >

          {/* HEADER */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
            <div>
              <h2 className="text-xl font-bold text-slate-950">
                Menu
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Explore ThinkQuotient AI
              </p>
            </div>

            <button
              onClick={toggleMobileMenu}
              className="w-10 h-10 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center hover:bg-slate-200 transition"
            >
              <X size={20} />
            </button>
          </div>

          {/* NAV */}
          <div className="p-5 overflow-y-auto h-[calc(100vh-80px)]">

            {/* HOME */}
            <Link
              to="/"
              onClick={toggleMobileMenu}
              className={`block px-5 py-4 rounded-2xl text-base font-semibold transition-all duration-300 mb-3
              ${isActive("/")
                  ? "bg-slate-950 text-white shadow-lg"
                  : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
            >
              Home
            </Link>
            {/* ABOUT US */}
            <Link
              to="/about-us"
              onClick={toggleMobileMenu}
              className={`block px-5 py-4 rounded-2xl text-base font-semibold transition-all duration-300 mb-3
              ${isActive("/about-us")
                  ? "bg-slate-950 text-white shadow-lg"
                  : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
            >
              About Us
            </Link>

            {/* CONTACT US */}
            <Link
              to="/contact-us"
              onClick={toggleMobileMenu}
              className={`block px-5 py-4 rounded-2xl text-base font-semibold transition-all duration-300 mb-3
              ${isActive("/contact-us")
                  ? "bg-slate-950 text-white shadow-lg"
                  : "bg-slate-50 text-slate-700 hover:bg-slate-100"
                }`}
            >
              Contact Us
            </Link>

            {/* AI FEATURES */}
            <div className="mb-3">
              <button
                onClick={() => setAiMenuOpen(!aiMenuOpen)}
                className="w-full flex items-center justify-between px-5 py-4 rounded-2xl bg-slate-50 text-slate-800 font-semibold hover:bg-slate-100 transition-all duration-300"
              >
                AI Features

                <ChevronDown
                  size={18}
                  className={`transition-transform duration-300 ${aiMenuOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {/* SUBMENU */}
              <div
                className={`overflow-hidden transition-all duration-300
                ${aiMenuOpen
                    ? "max-h-[500px] mt-3"
                    : "max-h-0"
                  }`}
              >
                <div className="space-y-2 pl-2">
                  {aiFeatures.map((item, index) => (
                    <Link
                      key={index}
                      to={item.path}
                      onClick={toggleMobileMenu}
                      className="block px-4 py-3 rounded-2xl text-sm font-semibold bg-white border border-slate-100 text-slate-700 shadow-sm hover:bg-slate-50 hover:text-slate-950 transition-all duration-300"
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {/* CONTACT CARD */}
            <div className="mt-6 rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 p-5 text-white">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Sparkles size={16} className="text-blue-300" />
                AI Automation Solutions
              </div>

              <div className="mt-4 space-y-3">
                {!countryLoading && office.phone && (
                  <a
                    href={office.phone.href}
                    className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 text-sm text-white/90"
                  >
                    <Phone size={15} />
                    {office.phone.display}
                  </a>
                )}

                <a
                  href="mailto:enquiry@thinkquotient.ai"
                  className="flex items-center gap-3 rounded-2xl bg-white/10 px-4 py-3 text-sm text-white/90"
                >
                  <Mail size={15} />
                  enquiry@thinkquotient.ai
                </a>
              </div>
            </div>

            {/* CTA */}
            {/* <Link
              to="/registerWebinar"
              onClick={toggleMobileMenu}
              className="mt-6 flex items-center justify-center gap-2 rounded-2xl bg-slate-950 text-white px-5 py-4 text-base font-semibold shadow-lg hover:bg-slate-800 transition-all duration-300"
            >
              <Sparkles size={18} />
              Register Free Webinar
            </Link> */}

          </div>
        </div>
      </div>

    </header>
  );
};

export default Head;